import { observer } from 'mobx-react-lite'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import {
  CustomSelect,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { Avatar } from '@/components/Avatar'
import { AllocationInputView } from '@/features/jobs/details/components/PayoutSplitAllocator/AllocationInputView'
import { type AllocationInputViewModel } from './AllocationInputViewModel'

export interface AllocationInputViewContainerProps {
  vm: AllocationInputViewModel
  isRemovable: boolean
}

export const AllocationInputViewContainer = observer(
  function AllocationInputViewContainer({
    vm,
    isRemovable,
  }: AllocationInputViewContainerProps) {
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource: vm.searchMembers,
      debounce: 300,
    })

    return (
      <AllocationInputView
        recipientSelect={
          <CustomSelect
            {...selectProps}
            searchResults={vm.excludeAlreadySelected(selectProps.searchResults)}
            placeholder="Select a member"
            multi={false}
            value={vm.recipient ?? undefined}
            onChange={vm.setRecipient}
            itemToString={(v) => String(v?.displayName)}
            testId="allocation-recipient-selector"
            status={vm.validation.getError('recipient') ? 'danger' : 'default'}
            renderItem={(itemProps) => (
              <SingleItemSelectedDefault
                {...itemProps}
                visual={<Avatar size={24} subject={itemProps.item} />}
              />
            )}
            renderSearchItem={({
              item,
              itemToString: _itemToString,
              ...itemProps
            }) => (
              <SearchResultItem
                {...itemProps}
                visual={<Avatar size={24} subject={item} />}
              >
                {item.displayName || `(User ID: ${item.publicId})`}
              </SearchResultItem>
            )}
          />
        }
        recipientError={vm.validation.getError('recipient')}
        isRemovable={isRemovable}
        onRemove={vm.remove}
        allocatedAmount={vm.allocatedAmount.value}
        allocatedPercentage={vm.allocatedAmount.percentage}
        allocatedAmountError={vm.validation.getError('allocatedAmount')}
        onAllocatedAmountChange={vm.allocatedAmount.setValue}
        onAllocatedPercentageChange={vm.allocatedAmount.setPercentage}
        authorizedPartialPayoutAmount={vm.authorizedPartialPayoutAmount.value}
        authorizedPartialPayoutPercentage={
          vm.authorizedPartialPayoutAmount.percentage
        }
        authorizedPartialPayoutAmountError={vm.validation.getError(
          'authorizedPartialPayoutAmount',
        )}
        onAuthorizedPartialPayoutAmountChange={
          vm.authorizedPartialPayoutAmount.setValue
        }
        onAuthorizedPartialPayoutPercentageChange={
          vm.authorizedPartialPayoutAmount.setPercentage
        }
      />
    )
  },
)
